import { useRef, useState } from 'react';
import { api } from '../../../../services/api';
import { classificarSefaz, classificarErroVeiculo, classificarRecusaDeCredito, mensagemDoBody, sanitizarErro } from './respostas';
import {
  lerEmissaoEmCurso, gravarEmissaoEmCurso, limparEmissaoEmCurso, registroDaTentativa,
  type EmissaoEmCurso, type ResumoDaViagem,
} from './retomada';
import { montarCorpoAtf, montarCorpoCteos } from './corpos';
import type { WizardState } from './types';

function gerarChave(): string {
  return `${Date.now()}_${Math.floor(Math.random() * 9_999_999)}`;
}

export type EstagioEmissao = 'ocioso' | 'criando-cte' | 'autorizando-sefaz' | 'solicitando-atf' | 'concluido';

export interface ResultadoEmissao {
  atfLogId: number;
  jobId: string | null;
}

interface UseEmissaoOpts {
  /** O cooperado logado: o registro da emissão parada é dele, e só dele. */
  idCooperado: number | null;
  onCreditoRecusado?: (mensagem: string, acao: string) => void;
  /**
   * Quando true, a recusa por saldo vira OFERTA de pagar a diferença em vez de
   * bloqueio. Estava fixo em `false` desde a 0034, e era isso que mandava o
   * cooperado sair do wizard para recarregar um pacote inteiro.
   */
  podeCobrirPorPix?: boolean;
}

/**
 * A sequência de 3 estágios (`_emitir()` do app) — porta arquitetural, não só
 * de regra: cada estágio é retomável independentemente, guardado por uma
 * chave de idempotência própria. Uma nova chamada é "rodar a mesma função de
 * novo": os guards decidem sozinhos o que pular.
 *
 * Desde o CT-e criado, cada estágio grava o registro da emissão no navegador
 * (`retomada.ts`). Reaberta a tela, a emissão parada **não** é restaurada em
 * silêncio: fica em `pendente` para a tela perguntar, porque numa viagem nova a
 * chave velha faria o servidor devolver o CT-e antigo.
 */
export function useEmissao({ idCooperado, onCreditoRecusado, podeCobrirPorPix = false }: UseEmissaoOpts) {
  const [estagio, setEstagio] = useState<EstagioEmissao>('ocioso');
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pendente, setPendente] = useState<EmissaoEmCurso | null>(
    () => (idCooperado == null ? null : lerEmissaoEmCurso(idCooperado, Date.now())),
  );
  const [desfechoDaRetomada, setDesfechoDaRetomada] = useState<'concluida' | 'recusada' | null>(null);
  /**
   * O CT-e desta tentativa já existe. Daí em diante a viagem não muda: voltar e
   * editar faria a ATF descrever outra viagem que o documento.
   */
  const [documentoEmitido, setDocumentoEmitido] = useState(false);

  const keyCteRef = useRef<string | null>(null);
  const keyAtfRef = useRef<string | null>(null);
  const numCteRef = useRef<string | null>(null);
  const idCteRef = useRef<number | null>(null);
  const serieRef = useRef<string | null>(null);
  const cteAutorizadoRef = useRef(false);
  /** Numa retomada, o corpo da ATF da tentativa original: a tela está vazia. */
  const corpoRetomadoRef = useRef<Record<string, unknown> | null>(null);
  const resumoRetomadoRef = useRef<ResumoDaViagem | null>(null);

  function zerarTentativa() {
    keyCteRef.current = null;
    keyAtfRef.current = null;
    numCteRef.current = null;
    idCteRef.current = null;
    serieRef.current = null;
    cteAutorizadoRef.current = false;
    corpoRetomadoRef.current = null;
    resumoRetomadoRef.current = null;
    setDocumentoEmitido(false);
    if (idCooperado != null) limparEmissaoEmCurso(idCooperado);
  }

  function reiniciar() {
    zerarTentativa();
    setEstagio('ocioso');
    setErro(null);
  }

  function corpoDaAtf(estado: WizardState | null): Record<string, unknown> | null {
    if (corpoRetomadoRef.current) return corpoRetomadoRef.current;
    if (!estado || !numCteRef.current) return null;
    return montarCorpoAtf(estado, numCteRef.current, serieRef.current ?? '');
  }

  function persistir(estado: WizardState | null) {
    const corpoAtf = corpoDaAtf(estado);
    if (!numCteRef.current || idCooperado == null || !corpoAtf) return;
    const resumo = resumoRetomadoRef.current ?? (estado
      ? { origem: estado.municipioOrigemNome, destino: estado.municipioDestinoNome, dataIda: estado.dataIda }
      : undefined);
    gravarEmissaoEmCurso(idCooperado, registroDaTentativa({
      numCte: numCteRef.current,
      keyCte: keyCteRef.current,
      keyAtf: keyAtfRef.current,
      idCte: idCteRef.current,
      serie: serieRef.current,
      cteAutorizado: cteAutorizadoRef.current,
    }, idCooperado, corpoAtf, Date.now(), resumo));
  }

  /**
   * Roda a sequência inteira. Retomável: se `numCteRef` já existir (de uma
   * tentativa anterior nesta sessão ou restaurada do navegador), pula direto
   * para o estágio seguinte — nunca cria um segundo CT-e para a mesma tentativa.
   *
   * `estado` nulo é a retomada: não há formulário, e o corpo da ATF é o gravado.
   */
  async function executar(estado: WizardState | null): Promise<ResultadoEmissao | null> {
    const retomando = estado == null;
    setErro(null);
    try {
      // ── Estágio 1: criar CT-e ──────────────────────────────────────
      if (!numCteRef.current) {
        if (!estado) throw new Error('Não há emissão para continuar.');
        setEstagio('criando-cte');
        setMensagem('Emitindo documentação...');
        keyCteRef.current ??= gerarChave();

        if (estado.idVeiculo == null) throw new Error('Veículo não selecionado');
        const corpo = montarCorpoCteos(estado, estado.idVeiculo, keyCteRef.current);

        try {
          const { data } = await api.post('/cooperado/cteos', corpo);
          numCteRef.current = String(data.numeroCTeOS ?? data.numeroCteOS ?? '');
          idCteRef.current = Number(data.idCTeOS ?? data.idCteOS);
          serieRef.current = String(data.serie ?? '');
          setDocumentoEmitido(true);
          // Daqui em diante existe numeração fiscal consumida: é o que precisa
          // sobreviver a recarregar a página.
          persistir(estado);
        } catch (e: any) {
          const status = e?.response?.status ?? 0;
          const corpo = e?.response?.data;
          const veiculoErro = classificarErroVeiculo(status, corpo);
          if (veiculoErro) {
            keyCteRef.current = null; // nada foi consumido — retry com chave nova
            throw new Error(corpo?.error || 'Veículo não pôde ser usado nesta emissão.');
          }
          const credito = classificarRecusaDeCredito({ status, corpo, exibeCredito: true, podeCobrirPorPix });
          if (credito) {
            keyCteRef.current = null;
            onCreditoRecusado?.(credito.mensagem, credito.acao);
            throw new Error(credito.mensagem);
          }
          keyCteRef.current = null;
          throw new Error(mensagemDoBody(corpo, 'Não foi possível emitir o documento fiscal.'));
        }
      }

      // ── Estágio 2: autorizar na SEFAZ ──────────────────────────────
      if (!cteAutorizadoRef.current) {
        setEstagio('autorizando-sefaz');
        setMensagem('Autorizando na SEFAZ...');

        const { data, status } = await api.post(`/cooperado/cteos/${idCteRef.current}/enviar`, {})
          .then(r => ({ data: r.data, status: r.status }))
          .catch(e => ({ data: e?.response?.data, status: e?.response?.status ?? 0 }));

        const { desfecho, mensagem: msgSefaz } = classificarSefaz(status || 200, data);
        if (desfecho === 'autorizado') {
          cteAutorizadoRef.current = true;
          persistir(estado);
        } else if (desfecho === 'indeterminado') {
          // A resposta nunca chegou — o documento PODE ter sido autorizado.
          // Nada é descartado; repetir pergunta de novo pelo mesmo documento.
          persistir(estado);
          throw new Error('Não foi possível confirmar a autorização na SEFAZ. Tente de novo em instantes: o mesmo documento é consultado, e nenhum outro é emitido.');
        } else {
          // Recusa definitiva — reinicia do zero, com chave nova.
          zerarTentativa();
          if (retomando) setDesfechoDaRetomada('recusada');
          throw new Error(msgSefaz);
        }
      }

      // ── Estágio 3: solicitar a ATF ──────────────────────────────────
      setEstagio('solicitando-atf');
      setMensagem('Solicitando a ATF...');
      // A chave da ATF é gravada ANTES do envio: se a resposta se perder e a
      // página recarregar, a próxima tentativa manda a mesma.
      if (!keyAtfRef.current) {
        keyAtfRef.current = gerarChave();
        persistir(estado);
      }

      const corpoAtf = corpoDaAtf(estado);
      if (!corpoAtf) throw new Error('Não há emissão para continuar.');
      try {
        const { data } = await api.post('/cooperado/atf', { ...corpoAtf, idempotencyKey: keyAtfRef.current });
        setEstagio('concluido');
        zerarTentativa();
        if (retomando) setDesfechoDaRetomada('concluida');
        return { atfLogId: data.atfLogId, jobId: data.jobId ?? null };
      } catch (e: any) {
        const status = e?.response?.status ?? 0;
        const corpo = e?.response?.data;
        const credito = classificarRecusaDeCredito({ status, corpo, exibeCredito: true, podeCobrirPorPix });
        if (credito) {
          // Na retomada não há trajeto na tela para montar a cobertura por Pix:
          // a recusa é só dita.
          if (!retomando) onCreditoRecusado?.(credito.mensagem, credito.acao);
          throw new Error(credito.mensagem);
        }
        // Falha aqui NÃO descarta o CT-e nem a chave — o documento fiscal já
        // existe; repetir reaproveita o mesmo numCte/keyAtf.
        throw new Error(mensagemDoBody(corpo, 'Não foi possível solicitar a ATF. O documento fiscal já saiu, e tentar de novo reaproveita o mesmo documento.'));
      }
    } catch (e: any) {
      const msg = e instanceof Error ? e.message : sanitizarErro(e);
      setErro(msg);
      setEstagio('ocioso');
      return null;
    }
  }

  /** Continua a emissão parada com o que foi gravado: mesmo CT-e, mesmas chaves. */
  function retomar(): Promise<ResultadoEmissao | null> {
    const r = pendente;
    if (!r) return Promise.resolve(null);
    keyCteRef.current = r.keyCte ?? null;
    keyAtfRef.current = r.keyAtf ?? null;
    numCteRef.current = r.numCte;
    idCteRef.current = r.idCte ?? null;
    serieRef.current = r.serie ?? null;
    cteAutorizadoRef.current = r.cteAutorizado;
    corpoRetomadoRef.current = r.corpoAtf ?? null;
    resumoRetomadoRef.current = r.resumo ?? null;
    return executar(null);
  }

  /**
   * O cooperado escolheu não continuar. O documento **continua existindo** no
   * servidor — some daqui, não de lá.
   */
  function descartarPendente() {
    zerarTentativa();
    setPendente(null);
    setDesfechoDaRetomada(null);
    setEstagio('ocioso');
    setErro(null);
  }

  return {
    estagio, mensagem, erro, documentoEmitido,
    emitir: (estado: WizardState) => executar(estado),
    reiniciar,
    pendente, desfechoDaRetomada, retomar, descartarPendente,
  };
}
